import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import ViewContainer from "../../../../../components/layout/view/ViewContainer";
import ViewHeader from "../../../../../components/layout/view/ViewHeader";
import EmptyState from "../../../../../components/feedback/EmptyState";
import ContentGrid from "../../../../../components/grids/content/ContentGrid";
import SearchField from "../../../../../components/input/searchField/SearchField";
import StatusAlert from "../../../../../components/alerts/StatusAlert";

import UserGroupCard from "../../../components/cards/groups/UserGroupCard";
import MemberJoinGroupModal from "../joinGroup/MemberJoinGroupModal";

import { useMemberGroups } from "../../hooks/useMemberGroups";

import "./MemberGroupsView.css";

const getMemberStatus = (group) =>
  String(group?.memberStatus ?? group?.membershipStatus ?? group?.status ?? "")
    .trim()
    .toLowerCase();

const isGroupOwner = (group) =>
  group?.isOwner === true || Number(group?.isOwner) === 1;

export default function MemberGroupsView({ userId }) {
  const navigate = useNavigate();

  const {
    groups,
    group,
    loading,
    error,
    setError,
    findGroupCode,
    getMemberGroups,
    leaveMemberGroup,
  } = useMemberGroups(userId);

  const getGroupsRef = useRef(getMemberGroups);
  getGroupsRef.current = getMemberGroups;

  const [code, setCode] = useState("");
  const [joinModalOpen, setJoinModalOpen] = useState(false);

  const groupList = Array.isArray(groups)
    ? groups.filter((item) => {
        const status = getMemberStatus(item);

        return (
          isGroupOwner(item) || status === "pending" || status === "approved"
        );
      })
    : [];

  const selectedGroup = group?.group ?? group;
  const normalizedCode = code.trim().toUpperCase();

  useEffect(() => {
    if (userId) {
      void getGroupsRef.current();
    }
  }, [userId]);

  const handleCodeChange = (value) => {
    setCode(typeof value === "string" ? value : (value?.target?.value ?? ""));

    if (error) setError("");
  };

  const handleFindGroup = async (event) => {
    event.preventDefault();

    if (!normalizedCode || loading) return;

    const result = await findGroupCode(normalizedCode);

    if (!result || result.success === false) return;

    if ((result.group ?? result)?.groupTag) {
      setJoinModalOpen(true);
    }
  };

  const handleJoinSuccess = async () => {
    setCode("");
    setJoinModalOpen(false);
    setError("");
    await getMemberGroups();
  };

  const handleOpenGroup = (item) => {
    if (!item?.groupTag) return;

    if (!isGroupOwner(item) && getMemberStatus(item) !== "approved") {
      return;
    }

    navigate(`/groups/${encodeURIComponent(item.groupTag)}`);
  };

  const handleLeaveGroup = async (event, item) => {
    event?.stopPropagation?.();

    if (!item?.groupTag || loading) return;

    const confirmed = window.confirm(
      `Are you sure you want to leave "${item.groupName || "this group"}"?`,
    );

    if (!confirmed) return;

    const result = await leaveMemberGroup(item.groupTag);

    if (result && result.success !== false) {
      await getMemberGroups();
    }
  };

  return (
    <div className="member-groups">
      <ViewContainer>
        <ViewHeader
          title="Access Group"
          description="Enter your group code to request access."
        />

        <form className="member-groups__join-form" onSubmit={handleFindGroup}>
          <SearchField
            placeholder="Enter group code"
            value={code}
            onChange={handleCodeChange}
            disabled={loading}
          />

          <button
            type="submit"
            className="member-groups__join-button"
            disabled={loading || !normalizedCode}
          >
            {loading ? "Finding..." : "Find Group"}
          </button>
        </form>

        {error && (
          <StatusAlert
            status={
              typeof error === "string"
                ? { type: "error", message: error }
                : error
            }
          />
        )}
      </ViewContainer>

      <ViewContainer>
        <ViewHeader
          title={`My Groups (${groupList.length})`}
          description="Groups you've joined and pending requests."
        />

        <ContentGrid
          items={groupList}
          loading={loading && groupList.length === 0}
          columns={4}
          tabletColumns={3}
          mobileColumns={2}
          gap={16}
          showEmpty={false}
          skeletonCount={8}
          renderItem={(item) => (
            <UserGroupCard
              key={item.groupTag}
              group={item}
              loading={loading}
              onClick={handleOpenGroup}
              onLeave={(event) => handleLeaveGroup(event, item)}
              showMenu
            />
          )}
        />

        {!loading && groupList.length === 0 && (
          <EmptyState
            title="No groups yet"
            description="Enter your group code to request access."
          />
        )}
      </ViewContainer>

      <MemberJoinGroupModal
        open={joinModalOpen}
        group={selectedGroup}
        userId={userId}
        onClose={() => {
          setJoinModalOpen(false);
          setError("");
        }}
        onSuccess={handleJoinSuccess}
      />
    </div>
  );
}
